import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Users,
  DollarSign,
  Star,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Building,
  Loader2,
  Expand,
  Minimize2,
} from 'lucide-react';
import { HRStaffService, type OrganizationDownlineNode, type OrganizationTreeResponse } from '../services/hr-staff.service';

interface OrganizationTreeViewProps {
  onError: (error: string) => void;
  onSelectNode?: (node: OrganizationDownlineNode) => void;
}

// Performance colors
const getPerformanceColor = (performance: 'overperforming' | 'normal' | 'inactive') => {
  switch (performance) {
    case 'overperforming':
      return 'bg-green-50 border-green-300 text-green-800';
    case 'inactive':
      return 'bg-gray-50 border-gray-300 text-gray-600';
    default:
      return 'bg-white border-gray-200 text-gray-800';
  }
};

const getPerformanceDot = (performance: 'overperforming' | 'normal' | 'inactive') => {
  switch (performance) {
    case 'overperforming':
      return 'bg-green-500';
    case 'inactive':
      return 'bg-gray-400';
    default:
      return 'bg-blue-500';
  }
};

const getOrgTypeBadge = (type?: string) => {
  if (!type) return null;
  const colors: Record<string, string> = {
    FMO: 'bg-purple-100 text-purple-800',
    EM: 'bg-blue-100 text-blue-800',
    DM: 'bg-green-100 text-green-800',
    CM: 'bg-yellow-100 text-yellow-800',
    BM: 'bg-orange-100 text-orange-800',
    AM: 'bg-red-100 text-red-800',
    M: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
      {type}
    </span>
  );
};

// Tree Node Component
const TreeNode: React.FC<{
  node: OrganizationDownlineNode;
  depth: number;
  onToggle: (nodeId: string) => void;
  onSelect?: (node: OrganizationDownlineNode) => void;
  expandedNodes: Set<string>;
}> = ({ node, depth, onToggle, onSelect, expandedNodes }) => {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  const performanceColorClass = getPerformanceColor(node.performance);
  const performanceDotClass = getPerformanceDot(node.performance);

  return (
    <div className="relative">
      {/* Connection Lines */}
      {depth > 0 && (
        <div
          className="absolute left-0 top-0 w-6 h-full"
          style={{ marginLeft: `${(depth - 1) * 32}px` }}
        >
          <div className="absolute left-3 top-0 w-px h-6 bg-gray-300"></div>
          <div className="absolute left-3 top-6 w-6 h-px bg-gray-300"></div>
        </div>
      )}

      {/* Node */}
      <div
        className="relative mb-2"
        style={{ marginLeft: `${depth * 32}px` }}
      >
        <div
          className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${performanceColorClass}`}
          onClick={() => hasChildren && onToggle(node.id)}
        >
          {/* Expand/Collapse Button */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {hasChildren ? (
              <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>

          {/* Avatar with Performance Indicator */}
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              {node.firstName[0]}{node.lastName[0]}
            </div>
            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${performanceDotClass}`}></div>
          </div>

          {/* Node Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-gray-900 truncate">
                {node.firstName} {node.lastName}
              </h4>
              {getOrgTypeBadge(node.organizationType)}
              {node.performance === 'overperforming' && (
                <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Top Performer
                </span>
              )}
              {node.performance === 'inactive' && (
                <span className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Inactive
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">{node.email}</p>
            {node.organizationName && (
              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                <Building className="w-3 h-3" />
                {node.organizationName}
              </p>
            )}

            {/* Expanded Details */}
            {isExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Team Size</p>
                    <p className="font-semibold text-gray-900">{node.teamSize}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded">
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Sales</p>
                    <p className="font-semibold text-gray-900">{node.totalSales}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-yellow-100 rounded">
                    <Star className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Approval Rate</p>
                    <p className="font-semibold text-gray-900">{node.approvalRating}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats (collapsed view) */}
          {!isExpanded && (
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{node.teamSize}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                <span>{node.totalSales}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                <span>{node.approvalRating}%</span>
              </div>
            </div>
          )}

          {/* Select Button */}
          {onSelect && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(node);
              }}
              className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Select
            </button>
          )}
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="relative">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onToggle={onToggle}
              onSelect={onSelect}
              expandedNodes={expandedNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Legend Component
const Legend: React.FC = () => (
  <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
    <span className="text-sm font-medium text-gray-700">Performance Legend:</span>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-green-500"></div>
      <span className="text-sm text-gray-600">Overperforming</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
      <span className="text-sm text-gray-600">Normal</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
      <span className="text-sm text-gray-600">Inactive</span>
    </div>
  </div>
);

export default function OrganizationTreeView({ onError, onSelectNode }: OrganizationTreeViewProps) {
  const [data, setData] = useState<OrganizationTreeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [depth, setDepth] = useState(10);

  const loadTree = useCallback(async () => {
    try {
      setLoading(true);
      const result = await HRStaffService.getOrganizationTree(undefined, depth);
      setData(result);
      // Auto-expand root node
      if (result.tree) {
        setExpandedNodes(new Set([result.tree.id]));
      }
    } catch (error: any) {
      console.error('Error loading organization tree:', error);
      onError(error.message || 'Failed to load organization tree');
    } finally {
      setLoading(false);
    }
  }, [depth, onError]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const handleToggle = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!data?.tree) return;
    const allIds = new Set<string>();
    const collectIds = (node: OrganizationDownlineNode) => {
      allIds.add(node.id);
      node.children.forEach(collectIds);
    };
    collectIds(data.tree);
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    if (!data?.tree) return;
    setExpandedNodes(new Set([data.tree.id]));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Organization Hierarchy</h2>
              <p className="text-sm text-gray-500">Full visibility of the entire organization structure</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Expand className="w-4 h-4" />
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Minimize2 className="w-4 h-4" />
              Collapse All
            </button>
            <button
              onClick={loadTree}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {data && (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Total Team Size</p>
              <p className="text-2xl font-bold text-blue-700">{data.totalTeamSize}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Total Sales</p>
              <p className="text-2xl font-bold text-green-700">{data.totalSales}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-600 font-medium">Avg. Approval Rating</p>
              <p className="text-2xl font-bold text-yellow-700">{data.averageApprovalRating}%</p>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <Legend />

      {/* Tree View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : data?.tree ? (
          <div className="overflow-x-auto">
            <TreeNode
              node={data.tree}
              depth={0}
              onToggle={handleToggle}
              onSelect={onSelectNode}
              expandedNodes={expandedNodes}
            />
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Building className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No organization hierarchy found</p>
          </div>
        )}
      </div>
    </div>
  );
}
